import { html, on, View } from 'rune-ts';
import { BannerTypeS } from '../../../../../features/Banner/outbound/share';
import klass from './DomainBanner.module.scss';
import { DomainBannerSwiperView } from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/DomainBannerSwiper.ts';
import {
  SwiperChangeEvent,
  SwiperView,
} from '@services/MarppleShop/renderApp/components/cells/Swiper/Swiper.ts';
import {
  DomainBannerImageViewMO,
  DomainBannerImageViewPC,
} from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/views/DomainBannerImageView.ts';
import {
  DomainBannerNavigationView,
  NavigateHandleEvent,
} from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/views/DomainBannerNavigationView.ts';
import { DomainBannerInformationView } from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/views/DomainBannerInformationView.ts';
import { DomainBannerPaginationView } from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/views/DomainBannerPaginationView.ts';

export type DomainBannerType = BannerTypeS.MainBannerData & { is_center: boolean };

interface DomainBannerViewData {
  banners: BannerTypeS.MainBannerData[];
  is_mobile: boolean;
}

export class DomainBannerView extends View<DomainBannerViewData> {
  swiperViewPC?: DomainBannerSwiperView<DomainBannerType, typeof DomainBannerImageViewPC>;
  swiperViewMO?: SwiperView<BannerTypeS.MainBannerData, typeof DomainBannerImageViewMO>;
  navigationView?: DomainBannerNavigationView;
  paginationView?: DomainBannerPaginationView;
  informationViews?: DomainBannerInformationView[];

  constructor(data: DomainBannerViewData) {
    super(data);
    const banners = data.banners.map((banner, idx) => ({ ...banner, is_center: idx >= 0 && idx < 3 }));
    const is_mobile = data.is_mobile;

    if (is_mobile) {
      const current_index = 0;

      this.paginationView = new DomainBannerPaginationView({
        current_index,
        max_index: data.banners.length - 1,
      });

      this.informationViews = data.banners.map(
        (banner, index) =>
          new DomainBannerInformationView({ information: banner, active: index === current_index }),
      );

      this.swiperViewMO = new SwiperView(
        {
          loop: true,
          speed: 800,
          autoplay:
            data.banners.length > 1
              ? {
                  delay: 3000,
                  pauseOnMouseEnter: true,
                  waitForTransition: true,
                  disableOnInteraction: false,
                }
              : false,
        },
        { swiper: klass.banner_cards_swiper, wrapper: klass.banner_cards_wrapper },
        DomainBannerImageViewMO,
        [...data.banners],
      );
    } else {
      this.swiperViewPC = new DomainBannerSwiperView(
        {
          loop: true,
          slidesPerView: 'auto',
          spaceBetween: 0,
          grabCursor: true,
          threshold: 5,
        },
        { swiper: klass.banner_cards_swiper, wrapper: klass.banner_cards_wrapper },
        DomainBannerImageViewPC,
        banners,
        {
          center_klass: klass.centered,
        },
      );

      this.navigationView = new DomainBannerNavigationView({
        prev_enable: data.banners.length > 1,
        next_enable: data.banners.length > 1,
      });
    }
  }

  protected override template() {
    const is_mobile = this.data.is_mobile;

    return is_mobile
      ? html`
          <section class="${klass.container}">
            ${this.informationViews} ${this.swiperViewMO}
            <div class="${klass.controller}">${this.data.banners.length > 1 ? this.paginationView : ''}</div>
          </section>
        `
      : html`
          <section class="${klass.container}">
            ${this.data.banners.length > 3
              ? html`${this.swiperViewPC}${this.navigationView}`
              : html`<div class="${klass.inner_container}">
                  ${new DomainBannerImageViewPC(
                    this.data.banners.map((banner) => ({ ...banner, is_center: true })),
                    {
                      center_klass: klass.centered,
                    },
                  )}
                </div>`}
          </section>
        `;
  }

  @on(SwiperChangeEvent)
  swiperHandler(e: SwiperChangeEvent) {
    const { current_index, previous_index } = e.detail;
    if (this.paginationView) {
      this.paginationView.setCurrentIndex(current_index);
      this.paginationView.redraw();
    }

    if (this.informationViews) {
      this.informationViews[previous_index]?.deactivate();
      this.informationViews[current_index]?.activate();
    }
  }

  @on(NavigateHandleEvent)
  navigationHandler(e: NavigateHandleEvent) {
    if (this.swiperViewPC) this.swiperViewPC[e.detail.direction]();
  }
}
